import { Component, inject, OnInit } from '@angular/core';
import {
    AbstractControl,
    FormBuilder,
    ReactiveFormsModule,
    ValidationErrors,
    Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
    MatDialogActions,
    MatDialogContent,
    MatDialogModule,
    MatDialogRef,
    MatDialogTitle,
} from '@angular/material/dialog';
import {
    MatError,
    MatFormFieldModule,
    MatLabel,
} from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { FuseValidators } from '@fuse/validators';
import { UserService } from '@globalConfig/features/users/services/api/user.service';
import { AlertService } from '@utils/alert-service/alert-service.service';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'app-change-password',
    templateUrl: './change-password.dialog.html',
    styles: [],
    standalone: true,
    imports: [
        MatDialogTitle,
        MatDialogContent,
        MatDialogActions,
        MatDialogModule,
        MatFormFieldModule,
        MatIconModule,
        MatButtonModule,
        MatLabel,
        MatInputModule,
        MatError,
        ReactiveFormsModule,
    ],
})
export class ChangePasswordDialog implements OnInit {
    readonly dialogRef = inject(MatDialogRef<ChangePasswordDialog>);
    private unsubscribeAll: Subject<any> = new Subject<any>();

    private formBuilder = inject(FormBuilder);
    private userService = inject(UserService);
    private alertService = inject(AlertService);

    passwordChangeForm = this.formBuilder.group(
        {
            oldPassword: ['', [Validators.required]],
            newPassword: [
                '',
                [
                    Validators.required,
                    Validators.minLength(8),
                    this.passwordValidator,
                ],
            ],
            confirmNewPassword: ['', [Validators.required]],
        },
        {
            validators: FuseValidators.mustMatch(
                'newPassword',
                'confirmNewPassword'
            ),
        }
    );

    ngOnInit(): void {
        this.passwordChangeForm.disable();
    }

    initialForm() {
        this.passwordChangeForm.patchValue({
            oldPassword: '',
            newPassword: '',
            confirmNewPassword: '',
        });
        this.passwordChangeForm.disable();
    }

    passwordValidator(control: AbstractControl): ValidationErrors | null {
        const password = control.value;
        const hasUpperCase = /[A-Z]/.test(password);
        const hasLowerCase = /[a-z]/.test(password);
        const hasNumber = /[1-9]/.test(password);

        if (password && (!hasUpperCase || !hasLowerCase || !hasNumber)) {
            return { passwordCase: true };
        }
        return null;
    }

    changePassword() {
        this.userService
            .changePassword({
                ...this.passwordChangeForm.getRawValue(),
            })
            .pipe(takeUntil(this.unsubscribeAll))
            .subscribe({
                next: async (response) => {
                    this.alertService.saveAlert(response.message);
                    this.dialogRef.close();
                },
                error: (error) => {
                    this.alertService.handleAlertResponse(error);
                },
            });
    }
}
